import { useState, useEffect } from 'react';
import { C } from '../data';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';

const S = {
  label: { fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:3 },
  body:  { fontFamily:"'Pretendard',sans-serif", fontSize:13, lineHeight:1.9, color:'#333' },
  muted: { fontFamily:"'Pretendard',sans-serif", fontSize:11, color:C.muted },
  badge: (color) => ({
    display:'inline-block', padding:'2px 8px',
    fontSize:10, fontFamily:"'Pretendard',sans-serif", letterSpacing:1,
    background: color === 'green' ? '#E6F4ED' : color === 'blue' ? '#E8EEF8' : '#F5F5F5',
    color: color === 'green' ? '#2E7D52' : color === 'blue' ? '#2C5282' : '#888',
    border: `1px solid ${color === 'green' ? '#B2D8C5' : color === 'blue' ? '#B2C8E8' : '#DDD'}`,
  }),
};

function fmtDate(ts) {
  return new Date(ts).toLocaleDateString('ko-KR', { year:'numeric', month:'2-digit', day:'2-digit' });
}

export default function BoardPage({ nav, onLogin }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('notices');       // 'notices' | 'inquiries'
  const [view, setView] = useState('list');         // 'list' | 'detail' | 'write'
  const [selected, setSelected] = useState(null);

  // 공지사항 상태
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(true);

  // 문의게시판 상태
  const [inquiries, setInquiries] = useState([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(true);
  const [replies, setReplies] = useState([]);

  // 문의 작성 폼
  const [writeTitle, setWriteTitle] = useState('');
  const [writeContent, setWriteContent] = useState('');
  const [writePublic, setWritePublic] = useState(true);
  const [writeSaving, setWriteSaving] = useState(false);
  const [writeMsg, setWriteMsg] = useState('');

  useEffect(() => {
    supabase.from('notices').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { setNotices(data || []); setNoticesLoading(false); });
  }, []);

  useEffect(() => {
    supabase.from('inquiries').select('*').order('created_at', { ascending: false })
      .then(({ data }) => { setInquiries(data || []); setInquiriesLoading(false); });
  }, []);

  const openDetail = async (post, type) => {
    setSelected({ ...post, _type: type });
    if (type === 'inquiry') {
      const { data } = await supabase.from('inquiry_replies')
        .select('*').eq('inquiry_id', post.id).order('created_at');
      setReplies(data || []);
    }
    setView('detail');
  };

  const goList = () => { setView('list'); setSelected(null); setReplies([]); };

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!writeTitle.trim() || !writeContent.trim()) { setWriteMsg('제목과 내용을 입력해주세요.'); return; }
    setWriteSaving(true); setWriteMsg('');
    const { error } = await supabase.from('inquiries').insert({
      user_id: user.id,
      title: writeTitle.trim(),
      content: writeContent.trim(),
      is_public: writePublic,
    });
    if (error) { setWriteMsg('등록 실패: ' + error.message); setWriteSaving(false); return; }
    // 새로고침
    const { data } = await supabase.from('inquiries').select('*').order('created_at', { ascending: false });
    setInquiries(data || []);
    setWriteTitle(''); setWriteContent(''); setWritePublic(true);
    setWriteSaving(false);
    setView('list');
  };

  const canSeeContent = (inq) => inq.is_public || inq.user_id === user?.id;

  return (
    <>
      <div style={{ maxWidth:900, margin:'0 auto', padding:'var(--page-top, 100px) 32px 80px', minHeight:'70vh' }}>
        {/* 탭 */}
        <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:36 }}>
          {[['notices','공지사항'],['inquiries','문의게시판']].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setView('list'); setSelected(null); }} style={{
              fontFamily:"'Pretendard',sans-serif", fontWeight:600, fontSize:13, letterSpacing:1,
              padding:'12px 28px', border:'none', cursor:'pointer', background:'none',
              color: tab === key ? C.ink : C.muted,
              borderBottom: tab === key ? `2px solid ${C.ink}` : '2px solid transparent',
              marginBottom:-1, transition:'color .15s',
            }}>{label}</button>
          ))}
        </div>

        {/* ── 공지사항 ── */}
        {tab === 'notices' && (
          view === 'list' ? (
            <>
              <p style={{ ...S.label, color:C.muted, marginBottom:20 }}>NOTICE</p>
              {noticesLoading ? (
                <p style={S.muted}>불러오는 중...</p>
              ) : notices.length === 0 ? (
                <p style={{ ...S.muted, padding:'60px 0', textAlign:'center' }}>등록된 공지사항이 없습니다.</p>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:`2px solid ${C.ink}` }}>
                      {['번호','제목','등록일'].map(h => (
                        <th key={h} style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:2, color:C.muted, padding:'8px 12px', textAlign: h==='제목' ? 'left' : 'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {notices.map((n, i) => (
                      <tr key={n.id} style={{ borderBottom:`1px solid ${C.border}`, cursor:'pointer' }}
                        onClick={() => openDetail(n, 'notice')}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <td style={{ ...S.muted, padding:'14px 12px', textAlign:'center', width:60 }}>{notices.length - i}</td>
                        <td style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, padding:'14px 12px', color:C.ink }}>{n.title}</td>
                        <td style={{ ...S.muted, padding:'14px 12px', textAlign:'center', whiteSpace:'nowrap' }}>{fmtDate(n.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <NoticeDetail notice={selected} onBack={goList} />
          )
        )}

        {/* ── 문의게시판 ── */}
        {tab === 'inquiries' && (
          view === 'list' ? (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <p style={{ ...S.label, color:C.muted }}>INQUIRY</p>
                {user ? (
                  <button onClick={() => setView('write')} style={{
                    padding:'8px 20px', background:C.ink, color:C.bg,
                    border:'none', cursor:'pointer',
                    fontFamily:"'Pretendard',sans-serif", fontSize:11, fontWeight:500, letterSpacing:2,
                  }}>문의 작성</button>
                ) : (
                  <button onClick={onLogin} style={{
                    padding:'8px 20px', background:'none', color:C.ink,
                    border:`1px solid ${C.border}`, cursor:'pointer',
                    fontFamily:"'Pretendard',sans-serif", fontSize:11, letterSpacing:1.5,
                  }}>로그인 후 문의</button>
                )}
              </div>
              {inquiriesLoading ? (
                <p style={S.muted}>불러오는 중...</p>
              ) : inquiries.length === 0 ? (
                <p style={{ ...S.muted, padding:'60px 0', textAlign:'center' }}>등록된 문의가 없습니다.</p>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:`2px solid ${C.ink}` }}>
                      {['번호','제목','상태','등록일'].map(h => (
                        <th key={h} style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:2, color:C.muted, padding:'8px 12px', textAlign: h==='제목' ? 'left' : 'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inq, i) => {
                      const visible = canSeeContent(inq);
                      return (
                        <tr key={inq.id} style={{ borderBottom:`1px solid ${C.border}`, cursor: visible ? 'pointer' : 'default' }}
                          onClick={() => visible && openDetail(inq, 'inquiry')}
                          onMouseEnter={e => { if (visible) e.currentTarget.style.background = C.bg2; }}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <td style={{ ...S.muted, padding:'14px 12px', textAlign:'center', width:60 }}>{inquiries.length - i}</td>
                          <td style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, padding:'14px 12px', color: visible ? C.ink : C.muted }}>
                            {!inq.is_public && <span style={{ fontSize:10, marginRight:6, color:'#888' }}>🔒</span>}
                            {visible ? inq.title : '비공개 문의입니다.'}
                          </td>
                          <td style={{ padding:'14px 12px', textAlign:'center' }}>
                            <span style={S.badge(inq.status === 'answered' ? 'green' : '')}>
                              {inq.status === 'answered' ? '답변 완료' : '답변 대기'}
                            </span>
                          </td>
                          <td style={{ ...S.muted, padding:'14px 12px', textAlign:'center', whiteSpace:'nowrap' }}>{fmtDate(inq.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          ) : view === 'detail' && selected ? (
            <InquiryDetail inquiry={selected} replies={replies} onBack={goList} user={user} />
          ) : view === 'write' ? (
            <form onSubmit={submitInquiry} style={{ maxWidth:640 }}>
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:28 }}>
                <button type="button" onClick={() => setView('list')} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:18, padding:0 }}>←</button>
                <p style={S.label}>문의 작성</p>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ ...S.label, fontSize:9, color:C.muted, display:'block', marginBottom:6 }}>제목 *</label>
                <input value={writeTitle} onChange={e => setWriteTitle(e.target.value)} required
                  placeholder="문의 제목을 입력하세요"
                  style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, fontFamily:"'Pretendard',sans-serif", fontSize:13, outline:'none', boxSizing:'border-box' }} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ ...S.label, fontSize:9, color:C.muted, display:'block', marginBottom:6 }}>내용 *</label>
                <textarea value={writeContent} onChange={e => setWriteContent(e.target.value)} required rows={8}
                  placeholder="문의 내용을 자세히 입력해주세요"
                  style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, fontFamily:"'Pretendard',sans-serif", fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }} />
              </div>
              <div style={{ display:'flex', gap:8, marginBottom:24 }}>
                <label style={{ ...S.label, fontSize:9, color:C.muted }}>공개 설정</label>
                {[['공개', true], ['비공개', false]].map(([label, val]) => (
                  <button key={label} type="button" onClick={() => setWritePublic(val)} style={{
                    padding:'6px 16px', border:`1px solid ${writePublic === val ? C.ink : C.border}`,
                    background: writePublic === val ? C.ink : 'none',
                    color: writePublic === val ? C.bg : C.ink,
                    cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontSize:11,
                  }}>{label}</button>
                ))}
              </div>
              {writeMsg && <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:'#C94040', marginBottom:12 }}>{writeMsg}</p>}
              <button type="submit" disabled={writeSaving} style={{
                width:'100%', padding:'13px 0', background:C.ink, color:C.bg, border:'none', cursor:'pointer',
                fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:2,
                opacity: writeSaving ? .6 : 1,
              }}>{writeSaving ? '등록 중...' : '문의 등록'}</button>
            </form>
          ) : null
        )}
      </div>
      <Footer nav={nav} />
    </>
  );
}

function NoticeDetail({ notice, onBack }) {
  if (!notice) return null;
  return (
    <div style={{ maxWidth:760 }}>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontFamily:"'Pretendard',sans-serif", fontSize:12, marginBottom:24, padding:0, display:'flex', alignItems:'center', gap:6 }}>
        ← 목록으로
      </button>
      <h2 style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:700, fontSize:18, marginBottom:12, color:C.ink }}>{notice.title}</h2>
      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:C.muted, marginBottom:28 }}>{fmtDate(notice.created_at)}</p>
      <div style={{ height:1, background:C.border, marginBottom:28 }} />
      {notice.image_url && (
        <img src={notice.image_url} alt="" style={{ width:'100%', maxWidth:720, display:'block', marginBottom:28, objectFit:'contain' }} />
      )}
      <div style={{ ...S.body, whiteSpace:'pre-wrap' }}>{notice.content}</div>
    </div>
  );
}

function InquiryDetail({ inquiry, replies, onBack, user }) {
  const isOwner = user?.id === inquiry.user_id;
  return (
    <div style={{ maxWidth:760 }}>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontFamily:"'Pretendard',sans-serif", fontSize:12, marginBottom:24, padding:0 }}>
        ← 목록으로
      </button>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <h2 style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:700, fontSize:18, color:C.ink }}>
          {!inquiry.is_public && <span style={{ fontSize:12, marginRight:6 }}>🔒</span>}
          {inquiry.title}
        </h2>
        <span style={S.badge(inquiry.status === 'answered' ? 'green' : '')}>
          {inquiry.status === 'answered' ? '답변 완료' : '답변 대기'}
        </span>
      </div>
      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:C.muted, marginBottom:28 }}>
        {fmtDate(inquiry.created_at)} {isOwner && <span style={{ marginLeft:8, color:'#888' }}>(내 문의)</span>}
      </p>
      <div style={{ height:1, background:C.border, marginBottom:24 }} />
      <div style={{ ...S.body, whiteSpace:'pre-wrap', marginBottom:36 }}>{inquiry.content}</div>

      {replies.length > 0 && replies.map(r => (
        <div key={r.id} style={{ background:C.bg2, border:`1px solid ${C.border}`, padding:'20px 24px', marginBottom:12 }}>
          <p style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:600, fontSize:11, letterSpacing:1.5, color:C.accent, marginBottom:12 }}>UNDERLOOKED 답변</p>
          <div style={{ ...S.body, whiteSpace:'pre-wrap' }}>{r.content}</div>
          <p style={{ ...S.muted, marginTop:12, fontSize:10 }}>{fmtDate(r.created_at)}</p>
        </div>
      ))}

      {inquiry.status === 'pending' && (
        <div style={{ padding:'20px 24px', background:'#FAFAFA', border:`1px solid ${C.border}` }}>
          <p style={{ ...S.muted, textAlign:'center' }}>답변 준비 중입니다. 빠른 시일 내에 답변 드리겠습니다.</p>
        </div>
      )}
    </div>
  );
}
